import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Shield, Clock, Globe, Check, Mic, MessageSquare, Type, Wand2 } from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import Logo from "../components/atoms/Logo";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";
import Mascot from "../components/atoms/Mascot";
import RephraseStudio from "./write/RephraseStudio";

const LEVELS = [
  { name: "Beginner",     days: "Days 1–12",  spine: "var(--sage)",     copy: "The alphabet's sound, the present tense, the fifty most-used verbs. You'll greet, order, ask, and answer." },
  { name: "Intermediate", days: "Days 13–28", spine: "var(--gold-500)", copy: "Tenses past and future, modals, conditionals. You start telling stories about your day in three sentences." },
  { name: "Advanced",     days: "Days 29–42", spine: "var(--rust)",     copy: "Phrasal verbs, idioms, business English, debate phrases. You hold a five-minute opinion without falling back." },
  { name: "IELTS",        days: "Days 43–50", spine: "var(--ink-800)",  copy: "The four bands, mock interviews, essay structure, examiner pet peeves. Bring your test date — we'll plan back from it." },
];

const FEATURES = [
  {
    icon: Mic, color: "var(--rust)",
    title: <>Speak with the <em>mic</em>, not the keyboard.</>,
    body: "Real-time speech recognition gives feedback word by word. The mic pulses in rust while it listens, then a sage chip appears with your score.",
  },
  {
    icon: MessageSquare, color: "var(--ink-700)",
    title: <>Twenty scenarios, no awkward role-play.</>,
    body: "Job interview, doctor's visit, customer support, ordering coffee. Pick the one closest to your week. The AI plays the other side; the grammar tip arrives in the margin.",
  },
  {
    icon: Type, color: "var(--gold-600)",
    title: <>A dictionary that explains <em>why.</em></>,
    body: "Tap any word — the IPA appears in mono, the example sentence in serif, and the etymology, when it helps, in three plain lines.",
  },
];

const TRUST = [
  { icon: Shield, label: "No credit card" },
  { icon: Clock,  label: "15 min/day" },
  { icon: Globe,  label: "Learn anywhere" },
  { icon: Check,  label: "Free forever for the first 12 lessons" },
];

export default function Landing() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash]);

  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <MarketingNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "80px 24px 64px" }}>
        <div className="grid items-center" style={{ gridTemplateColumns: "1.4fr 1fr", gap: 48 }}>
          <div>
            <Chip tone="gold">60 lessons · 30 badges · real-time voice</Chip>
            <h1
              className="font-display"
              style={{ fontSize: "clamp(48px, 7vw, 84px)", lineHeight: 0.98, letterSpacing: "-0.025em", margin: "18px 0 22px", maxWidth: "14ch", fontWeight: 400 }}
            >
              Learn English, <em style={{ color: "var(--gold-500)" }}>spoken with confidence.</em>
            </h1>
            <p style={{ fontSize: 19, lineHeight: 1.6, color: "var(--fg-2)", maxWidth: "52ch", margin: "0 0 28px" }}>
              A 50-day curriculum, 200+ grammar rules, twenty conversation scenarios, and a tutor who actually listens. Zero to IELTS-ready, on your own time.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/onboarding" style={{ textDecoration: "none" }}>
                <Button variant="primary" size="lg" iconRight={<ArrowRight size={16} strokeWidth={1.8} />}>
                  Pull up a chair
                </Button>
              </Link>
              <Link to="/login" style={{ textDecoration: "none" }}>
                <Button variant="ghost" size="lg">I have an account</Button>
              </Link>
            </div>
            <div style={{ marginTop: 56, display: "flex", gap: 32, flexWrap: "wrap", color: "var(--fg-3)", fontSize: 13 }}>
              {TRUST.map(({ icon: Icon, label }) => (
                <span key={label} style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <Icon size={16} strokeWidth={1.5} /> {label}
                </span>
              ))}
            </div>
          </div>
          <div className="hidden md:flex" style={{ justifyContent: "center" }}>
            <Mascot size={280} />
          </div>
        </div>
      </section>

      {/* Activation moment — anonymous Rephrase widget */}
      <section id="try" style={{ background: "var(--paper-deep)", borderTop: "1px solid var(--border-2)", borderBottom: "1px solid var(--border-2)", padding: "80px 0" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <Chip tone="gold" icon={<Wand2 size={13} strokeWidth={1.8} />}>Try it now · no signup</Chip>
            <h2 className="font-display" style={{ fontSize: "clamp(36px, 5vw, 52px)", lineHeight: 1.05, letterSpacing: "-0.02em", margin: "16px 0 12px", fontWeight: 400 }}>
              Paste a message. <em style={{ color: "var(--gold-500)" }}>Get it in three tones.</em>
            </h2>
            <p style={{ fontSize: 17, color: "var(--fg-2)", maxWidth: "52ch", margin: "0 auto", lineHeight: 1.6 }}>
              Hinglish, broken English, half-finished thoughts — pasted and polished. Seven more tones unlock with a free account.
            </p>
          </div>
          <RephraseStudio embedded anonymous />
        </div>
      </section>

      {/* Stats */}
      <section style={{ background: "var(--paper)", borderBottom: "1px solid var(--border-2)", padding: "56px 0" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid text-center" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 32 }}>
          {[
            { num: "50", em: null, label: "Daily lessons" },
            { num: "102", em: "k", label: "Dictionary words" },
            { num: "200+", em: null, label: "Grammar rules" },
            { num: "₹", em: "0", label: "For the first 12" },
          ].map((s) => (
            <div key={s.label} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div className="font-display" style={{ fontSize: 56, lineHeight: 1, color: "var(--ink-900)", fontWeight: 400 }}>
                {s.num}{s.em && <em style={{ color: "var(--gold-500)" }}>{s.em}</em>}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.12em", marginTop: 8 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Levels */}
      <section id="levels" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "96px 24px" }}>
        <Chip tone="gold">Curriculum</Chip>
        <h2 className="font-display" style={{ fontSize: 44, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "12px 0 12px", maxWidth: "18ch", fontWeight: 400 }}>
          Twelve weeks. <em style={{ color: "var(--gold-500)" }}>Four levels.</em> One steady climb.
        </h2>
        <p style={{ fontSize: 17, color: "var(--fg-2)", maxWidth: "56ch", lineHeight: 1.6 }}>
          Each level adds a layer — phonics first, then grammar, then real conversation, then exam prep. The level color follows you up: calmer at the start, more saturated as commitment grows.
        </p>

        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 32 }}>
          {LEVELS.map((l) => (
            <Card key={l.name} paperDeep spineColor={l.spine} style={{ paddingTop: 32, minHeight: 200, display: "flex", flexDirection: "column" }}>
              <h3 className="font-display" style={{ fontSize: 28, fontWeight: 400, margin: "0 0 4px" }}>{l.name}</h3>
              <div className="font-display" style={{ fontStyle: "italic", color: "var(--fg-3)", marginBottom: 14 }}>{l.days}</div>
              <p style={{ color: "var(--fg-2)", fontSize: 14, lineHeight: 1.5, margin: 0 }}>{l.copy}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ background: "var(--paper-deep)", padding: "96px 0" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chip tone="gold">What's inside</Chip>
          <h2 className="font-display" style={{ fontSize: 44, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "12px 0 32px", maxWidth: "20ch", fontWeight: 400 }}>
            A coach, a dictionary, and a real microphone — <em style={{ color: "var(--gold-500)" }}>in one tab.</em>
          </h2>
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {FEATURES.map((f, i) => (
              <Card key={i}>
                <f.icon size={28} strokeWidth={1.5} color={f.color} />
                <h3 className="font-display" style={{ fontSize: 24, fontWeight: 400, margin: "14px 0 6px", lineHeight: 1.15 }}>
                  {f.title}
                </h3>
                <p style={{ color: "var(--fg-2)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>{f.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* API teaser */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "96px 24px" }}>
        <div style={{ background: "var(--ink-900)", color: "var(--paper)", borderRadius: "var(--radius-xl)", padding: "48px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "center" }}>
          <div>
            <span className="eyebrow" style={{ color: "var(--gold-300)" }}>For developers</span>
            <h2 className="font-display" style={{ fontSize: 38, fontWeight: 400, margin: "8px 0 14px", color: "var(--paper)", lineHeight: 1.1 }}>
              The same engine. <em style={{ color: "var(--gold-300)" }}>One API call.</em>
            </h2>
            <p style={{ color: "rgba(251,246,232,0.78)", fontSize: 15, lineHeight: 1.65, margin: "0 0 22px", maxWidth: "44ch" }}>
              102K-word dictionary, 200 grammar rules, 20 conversation scenarios, real speech scoring. Send a clip and a sentence; get a band score in 200ms.
            </p>
            <Link to="/api-docs" style={{ textDecoration: "none" }}>
              <Button variant="primary" iconRight={<ArrowRight size={14} strokeWidth={1.8} />}>Get a key</Button>
            </Link>
          </div>
          <div style={{ background: "rgba(251,246,232,0.04)", border: "1px solid rgba(251,246,232,0.10)", borderRadius: "var(--radius-md)", padding: 18, fontFamily: "var(--font-mono)", fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ color: "rgba(251,246,232,0.40)" }}># Score speech against a target sentence</div>
            <div style={{ marginTop: 8 }}><span style={{ color: "var(--rust-light)" }}>POST</span> <span style={{ color: "var(--gold-300)" }}>/v1/speech/score</span></div>
            <div style={{ marginTop: 8, color: "rgba(251,246,232,0.40)" }}># Response · ~180ms</div>
            <div style={{ color: "var(--paper)" }}>{"{"}</div>
            <div style={{ paddingLeft: 16, color: "var(--sky-light)" }}>"score": <span style={{ color: "var(--rust-light)" }}>87</span>,</div>
            <div style={{ paddingLeft: 16, color: "var(--sky-light)" }}>"band": <span style={{ color: "var(--gold-300)" }}>"sage"</span></div>
            <div style={{ color: "var(--paper)" }}>{"}"}</div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "0 24px 96px", textAlign: "center" }}>
        <h2 className="font-display" style={{ fontSize: 44, lineHeight: 1.1, fontWeight: 400, margin: "0 auto 14px", maxWidth: "20ch" }}>
          Twelve free lessons. <em style={{ color: "var(--gold-500)" }}>One quiet hour a day.</em>
        </h2>
        <p style={{ fontSize: 17, color: "var(--fg-2)", maxWidth: "52ch", margin: "0 auto 28px", lineHeight: 1.6 }}>
          No card. No pop-ups. Sign up, take a placement test, and start tomorrow morning.
        </p>
        <Link to="/onboarding" style={{ textDecoration: "none" }}>
          <Button variant="primary" size="lg">Start free</Button>
        </Link>
      </section>

      {/* Footer */}
      <footer style={{ background: "var(--ink-900)", color: "var(--paper)", padding: "48px 0" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 32 }}>
          <div>
            <Logo dark />
            <p style={{ marginTop: 14, fontSize: 14, color: "rgba(251,246,232,0.60)", maxWidth: "42ch" }}>
              From zero to confident English speaker. Free for the first twelve lessons. Built warm in India by K2TechInfo.
            </p>
          </div>
          <FooterCol title="Learn">
            <FooterLink to="/onboarding">Start free</FooterLink>
            <FooterLink to="/login">Sign in</FooterLink>
            <FooterLink to="/api-docs">API for developers</FooterLink>
          </FooterCol>
          <FooterCol title="Product">
            <FooterLink to="/pricing">Pricing</FooterLink>
            <FooterLink to="/templates">Email templates</FooterLink>
            <FooterLink to="/about">About</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
          </FooterCol>
          <FooterCol title="Surface">
            <a href="https://k2techinfo.com" style={{ display: "block", color: "rgba(251,246,232,0.65)", fontSize: 13, marginBottom: 8, textDecoration: "none" }}>K2TechInfo</a>
            <FooterLink to="/contact">Report an issue</FooterLink>
          </FooterCol>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(251,246,232,0.10)", fontSize: 12, color: "rgba(251,246,232,0.45)" }}>
          © {new Date().getFullYear()} SpeakEasy. Made warm in India.
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h4 style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(251,246,232,0.45)", margin: "0 0 12px" }}>{title}</h4>
      {children}
    </div>
  );
}
function FooterLink({ to, children }) {
  return (
    <Link to={to} style={{ display: "block", color: "rgba(251,246,232,0.65)", fontSize: 13, marginBottom: 8, textDecoration: "none" }}>
      {children}
    </Link>
  );
}
